/**
 * MatrixFilterMode Component
 *
 * Advanced hexagon classification component supporting two filter modes:
 * - Matrix (2D): Two-property classification with bands and thresholds
 * - Simple (1D): Single-property classification with min/max ranges
 *
 * Features:
 * - Real-time hexagon classification and visualization
 * - Template save/load functionality
 * - PDF and DOCX report export
 * - Per-class pricing calculations
 * - Adjustable opacity per class
 *
 * Data flow:
 *   filter state → useMatrixClassification (classifies hexagons, applies styles,
 *   updates filterBoxes in parent) → classificationResults → editor components
 *
 * Hook orchestration:
 *   - useMatrixClassification: classification + hexagon styling
 *   - usePropertyRanges: computes min/max for selected properties (×3 calls)
 *   - useAvailableProperties: derives dropdown options from hexagon data
 *   - useCurrencies: provides currency list (will swap to API, Requirement 9)
 *   - useMatrixTemplates: template CRUD operations
 *
 * Parent communication (ref):
 *   This component exposes an imperative handle via forwardRef so the parent
 *   (HexagonColorFilter) can trigger the export modal from the "Generate PDF"
 *   button that lives in the parent's layout.
 *
 *   Why ref instead of lifting state or callback props:
 *   - The export data (filter, classificationResults, projectNotes, currency)
 *     is owned by this component and changes frequently during editing.
 *   - Lifting this state to the parent would create unnecessary coupling and
 *     re-renders in the parent on every filter tweak.
 *   - A useEffect syncing data upward is a React anti-pattern (extra renders,
 *     stale closures, data echo).
 *   - The ref pattern is already established in this codebase
 *     (see HexagonColorFilterLayer.jsx in the same parent).
 *   - The parent only needs to trigger an action ("open export modal"),
 *     not read or transform the child's state — imperative handle is the
 *     idiomatic React pattern for this use case.
 *
 *   Pros:
 *   - No state duplication or syncing between parent and child
 *   - Export logic stays co-located with the data it depends on
 *   - Parent remains decoupled from matrix filter internals
 *   - Consistent with existing codebase patterns
 *
 *   Cons:
 *   - Imperative (parent calls child method) rather than declarative
 *   - If MatrixFilterMode is refactored, the ref contract must be maintained
 *
 *   TODO (future improvement):
 *   - If the export logic needs to be reused from other components (e.g.,
 *     scheduled reports, project-level export), extract it into a standalone
 *     useMatrixPDFExport hook that accepts data as parameters. The hook would
 *     encapsulate map capture + data preparation + PDF generation, and could
 *     be called from any component that has access to the required data.
 *
 * @param {Object} props
 * @param {Array} props.hexagons - Array of hexagon features to classify
 * @param {number} props.hexagonSize - Size of each hexagon in m²
 * @param {Function} props.setFilterBoxes - Callback to update parent's filter boxes state
 * @param {Function} props.onFilterChange - Optional callback when filter changes
 * @param {boolean} props.isDemo - Whether in demo mode (disables saving)
 * @param {React.Ref} ref - Imperative handle exposing { openExportModal }
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { ButtonGroup } from 'react-bootstrap';

import {
	createEmptyMatrixFilter,
	createSimpleFilter,
	DEFAULT_PRESET,
	DEFAULT_SIMPLE_PRESET,
	getSimpleFilterCoverageGaps,
	getMatrixFilterCoverageGaps,
} from './matrixClassification';
import useMatrixClassification from './useMatrixClassification';
import usePropertyRanges from './usePropertyRanges';
import usePerBandPropertyRanges from './usePerBandPropertyRanges';
import useAvailableProperties from './useAvailableProperties';
import useCurrencies from './useCurrencies';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';
import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import { Section, SectionTitle, StatsTable, ColorDot } from './StyledMatrixFilterComponents';
import {
	captureMapCanvas,
	captureMapCanvasAsBuffer,
	prepareExportData,
	generatePDFContent,
	exportToDOCX,
} from './utils';
import useMatrixTemplates from './useMatrixTemplates';
import ExportReportModal from './ExportReportModal';
import SaveTemplateModal from './SaveTemplateModal';
import SimpleEditor from './SimpleEditor';
import MatrixEditor from './MatrixEditor';

export default forwardRef(function MatrixFilterMode(
	{ hexagons, hexagonSize, setFilterBoxes, onFilterChange, isDemo },
	ref
) {
	const [filter, setFilter] = useState(() => JSON.parse(JSON.stringify(DEFAULT_PRESET)));
	const [showEditor, setShowEditor] = useState(false);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [showExportModal, setShowExportModal] = useState(false);
	const [activeTemplate, setActiveTemplate] = useState(null);
	const [selectedPreset, setSelectedPreset] = useState('');
	const [isExporting, setIsExporting] = useState(false);
	const [projectNotes, setProjectNotes] = useState('');
	const [exportTheme, setExportTheme] = useState('dark');

	const { addToast } = useToast();
	const { confirm } = useConfirm();
	const { project, pickedTask, hexagonInfo, mapObject } = useProject();
	const { currentUser } = useAuth();

	// Template management
	const { matrixTemplates, saveMutation, saveTemplate, deleteMutation, loadTemplate } =
		useMatrixTemplates({
			activeTemplate,
			setActiveTemplate,
			setFilter,
			setShowSaveModal,
			hexagonSize,
			filter,
		});

	// Get current currency
	const currencies = useCurrencies();
	const currency = currencies.find(c => c.id === filter.currency) || currencies[0];

	// Generate PDF report
	const exportPDF = async (theme = 'dark') => {
		setIsExporting(true);
		try {
			const mapImageData = captureMapCanvas(mapObject);
			const exportData = prepareExportData({
				filter,
				classificationResults,
				project,
				currentUser,
				pickedTask,
				hexagonInfo,
				hexagonSize,
				projectNotes,
				currency,
				availableProperties,
				coverageGaps,
			});

			const htmlContent = generatePDFContent(exportData, theme, mapImageData);

			// Open in new window with printable content
			const printWindow = window.open('', '_blank');
			if (!printWindow) {
				addToast({ title: 'Please allow popups for this site', bg: 'warning' });
				return;
			}

			printWindow.document.write(htmlContent);
			printWindow.document.close();

			addToast({ title: 'Report opened in new tab', bg: 'success' });
		} catch (err) {
			console.error('PDF export error:', err);
			addToast({ title: 'Failed to generate report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	// Generate DOCX report
	const exportDOCX = async () => {
		setIsExporting(true);
		try {
			const mapImageBuffer = captureMapCanvasAsBuffer(mapObject);
			const exportData = prepareExportData({
				filter,
				classificationResults,
				project,
				currentUser,
				pickedTask,
				hexagonInfo,
				hexagonSize,
				projectNotes,
				currency,
				availableProperties,
				coverageGaps,
			});

			await exportToDOCX(exportData, mapImageBuffer);

			addToast({ title: 'DOCX report downloaded', bg: 'success' });
		} catch (err) {
			console.error('DOCX export error:', err);
			addToast({ title: 'Failed to generate DOCX report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	// Handle export with selected options
	const handleExport = format => {
		setShowExportModal(false);
		if (format === 'pdf') {
			exportPDF(exportTheme);
		} else if (format === 'docx') {
			exportDOCX();
		}
	};

	// Stable callback for opening export modal (passed to editor components)
	const openExportModal = useCallback(() => setShowExportModal(true), []);

	// Expose export trigger to parent via imperative handle.
	// The parent's "Generate PDF" button calls this to open the export modal.
	useImperativeHandle(
		ref,
		() => ({
			openExportModal,
		}),
		[openExportModal]
	);

	// Property ranges for editor display
	const propertyRange = usePropertyRanges(hexagons, filter.property);
	const bandRange = usePropertyRanges(hexagons, filter.bandProperty);
	const thresholdRange = usePropertyRanges(hexagons, filter.thresholdProperty);

	// Per-band threshold ranges for accurate coverage gap detection
	const perBandThresholdRanges = usePerBandPropertyRanges(
		hexagons,
		filter.bandProperty,
		filter.thresholdProperty,
		filter.bands
	);

	// Classification logic (extracted to useMatrixClassification hook)
	const classificationResults = useMatrixClassification({
		hexagons,
		hexagonSize,
		filter,
		setFilterBoxes,
	});

	// Notify parent of filter changes
	useEffect(() => {
		if (onFilterChange) onFilterChange(filter);
	}, [filter, onFilterChange]);

	// Stable callback for updating filter (passed to editor components)
	const updateFilter = useCallback(updates => {
		setFilter(prev => ({ ...prev, ...updates }));
	}, []);

	// isReady is false while the list is the static placeholder — see the hook.
	const { properties: availableProperties, isReady: propertiesReady } =
		useAvailableProperties(hexagons);

	// Snap the selected property to what the data actually supports. Presets
	// default to a property (e.g. 'volume_m3') a project's metadata may not have;
	// the <select> then shows its first option while filter.property keeps the
	// stale value, so classification runs against a property with no data.
	//
	// Gated on propertiesReady, not list length: the placeholder list omits the
	// dynamic per-class keys, so reconciling against it would clobber a valid one.
	useEffect(() => {
		if (!propertiesReady || !availableProperties.length) return;

		const has = id => availableProperties.some(p => p.id === id);
		const fallback = availableProperties[0].id;
		const updates = {};

		if (filter.property !== undefined && !has(filter.property)) {
			updates.property = fallback;
		}
		if (filter.bandProperty !== undefined && !has(filter.bandProperty)) {
			updates.bandProperty = fallback;
		}
		if (filter.thresholdProperty !== undefined && !has(filter.thresholdProperty)) {
			updates.thresholdProperty = fallback;
		}

		if (Object.keys(updates).length > 0) {
			setFilter(prev => ({ ...prev, ...updates }));
		}
	}, [
		propertiesReady,
		availableProperties,
		filter.property,
		filter.bandProperty,
		filter.thresholdProperty,
	]);

	const bandPropertyLabel =
		availableProperties.find(p => p.id === filter.bandProperty)?.label || filter.bandProperty;
	const thresholdPropertyLabel =
		availableProperties.find(p => p.id === filter.thresholdProperty)?.label ||
		filter.thresholdProperty;
	const isSimpleMode = filter.type === 'simple';

	// Detect sub-ranges of the actual data that no class covers. Computed
	// once here (rather than inside each editor) so the same result can be
	// shown in the editor's warning AND reused verbatim in the exported
	// report — the report will list the same gap(s) the user saw while
	// configuring the filter, instead of recomputing it separately.
	const coverageGaps = useMemo(
		() =>
			isSimpleMode
				? getSimpleFilterCoverageGaps(filter.classes, propertyRange)
				: getMatrixFilterCoverageGaps(filter, perBandThresholdRanges),
		[isSimpleMode, filter, propertyRange, perBandThresholdRanges]
	);

	return (
		<div>
			{/* Filter Mode Toggle */}
			<Section>
				<SectionTitle>Filter mode</SectionTitle>
				<ButtonGroup className="w-100 mb-2">
					<Button
						variant={!isSimpleMode ? 'primary' : 'outline-secondary'}
						size="sm"
						onClick={() => {
							if (isSimpleMode) {
								setFilter(JSON.parse(JSON.stringify(DEFAULT_PRESET)));
								setActiveTemplate(null);
								setSelectedPreset('');
								setProjectNotes('');
							}
						}}>
						Matrix (2D)
					</Button>
					<Button
						variant={isSimpleMode ? 'primary' : 'outline-secondary'}
						size="sm"
						onClick={() => {
							if (!isSimpleMode) {
								setFilter(JSON.parse(JSON.stringify(DEFAULT_SIMPLE_PRESET)));
								setActiveTemplate(null);
								setSelectedPreset('');
								setProjectNotes('');
							}
						}}>
						Simple (1D)
					</Button>
				</ButtonGroup>
			</Section>

			{/* Template Selection */}
			<Section>
				<SectionTitle>Filter template</SectionTitle>
				<Form.Select
					size="sm"
					value={activeTemplate?.uuid || selectedPreset}
					onChange={e => {
						const value = e.target.value;
						if (value === 'default') {
							setFilter(JSON.parse(JSON.stringify(DEFAULT_PRESET)));
							setActiveTemplate(null);
							setSelectedPreset('default');
							setProjectNotes('');
						} else if (value === 'simple_default') {
							setFilter(JSON.parse(JSON.stringify(DEFAULT_SIMPLE_PRESET)));
							setActiveTemplate(null);
							setSelectedPreset('simple_default');
							setProjectNotes('');
						} else if (value === 'empty') {
							setFilter(createEmptyMatrixFilter());
							setActiveTemplate(null);
							setSelectedPreset('empty');
							setProjectNotes('');
						} else if (value === 'simple_empty') {
							setFilter(createSimpleFilter());
							setActiveTemplate(null);
							setSelectedPreset('simple_empty');
							setProjectNotes('');
						} else if (value) {
							const template = matrixTemplates.find(t => t.uuid === value);
							if (template) loadTemplate(template);
							setSelectedPreset('');
							setProjectNotes('');
						} else {
							setSelectedPreset('');
						}
					}}>
					<option value="">-- Select template --</option>
					{!isSimpleMode && (
						<optgroup label="Matrix Presets (2D)">
							<option value="default">Default Matrix (5 Classes)</option>
							<option value="empty">Empty Matrix</option>
						</optgroup>
					)}
					{isSimpleMode && (
						<optgroup label="Simple Presets (1D)">
							<option value="simple_default">Volume Classification</option>
							<option value="simple_empty">Empty Simple Filter</option>
						</optgroup>
					)}
					{matrixTemplates.filter(t =>
						isSimpleMode
							? t.matrixConfig?.type === 'simple'
							: t.matrixConfig?.type === 'matrix'
					).length > 0 && (
						<optgroup label="Saved templates">
							{matrixTemplates
								.filter(t =>
									isSimpleMode
										? t.matrixConfig?.type === 'simple'
										: t.matrixConfig?.type === 'matrix'
								)
								.map(t => (
									<option key={t.uuid} value={t.uuid}>
										{t.title}
									</option>
								))}
						</optgroup>
					)}
				</Form.Select>

				{/* Save/Delete buttons */}
				<div className="d-flex gap-2 mt-2">
					<Button
						variant="outline-success"
						size="sm"
						className="flex-fill"
						onClick={() => setShowSaveModal(true)}
						disabled={isDemo}>
						{activeTemplate ? '💾 Update template' : '💾 Save as template'}
					</Button>
					{activeTemplate && (
						<Button
							variant="outline-danger"
							size="sm"
							onClick={async () => {
								const userConfirmed = await confirm({
									title: 'Delete Filter Template',
									message: (
										<>
											Are you sure you want to delete the filter template{' '}
											<strong>{activeTemplate.title}</strong>?
										</>
									),
								});
								if (userConfirmed) {
									deleteMutation.mutate(activeTemplate.uuid);
								}
							}}
							disabled={isDemo}>
							🗑️
						</Button>
					)}
				</div>
			</Section>

			{/* Results Summary */}
			<Section>
				<SectionTitle>Classification Results</SectionTitle>
				<Card>
					<Card.Body className="p-0">
						<StatsTable striped hover size="sm" className="mb-0">
							<thead>
								<tr>
									<th>Class</th>
									<th>Count</th>
									<th>Area</th>
									<th>%</th>
								</tr>
							</thead>
							<tbody>
								{filter.classes.map(cls => {
									const r = classificationResults[cls.id];
									return (
										<tr key={cls.id}>
											<td>
												<ColorDot color={cls.color} />
												{cls.label}
											</td>
											<td>{r?.count || 0}</td>
											<td>{(r?.totalArea || 0).toLocaleString()} m²</td>
											<td>{r?.areaPercentage || 0}%</td>
										</tr>
									);
								})}
								{classificationResults.below_minimum?.count > 0 && (
									<tr className="text-muted">
										<td>
											<ColorDot color="#808080" />
											Below Minimum
										</td>
										<td>{classificationResults.below_minimum.count}</td>
										<td>
											{classificationResults.below_minimum.totalArea.toLocaleString()}{' '}
											m²
										</td>
										<td>
											{classificationResults.below_minimum.areaPercentage}%
										</td>
									</tr>
								)}
								{classificationResults.unclassified?.count > 0 && (
									<tr className="text-muted">
										<td>
											<ColorDot color="#646464" />
											Unclassified
										</td>
										<td>{classificationResults.unclassified.count}</td>
										<td>
											{classificationResults.unclassified.totalArea.toLocaleString()}{' '}
											m²
										</td>
										<td>
											{classificationResults.unclassified.areaPercentage}%
										</td>
									</tr>
								)}
							</tbody>
						</StatsTable>
					</Card.Body>
				</Card>
			</Section>

			{/* Toggle Editor */}
			<Button
				variant="outline-secondary"
				size="sm"
				className="w-100 mb-3"
				onClick={() => setShowEditor(true)}>
				⚙️ Edit filter
			</Button>

			{/* Editor Offcanvas (Bottom Drawer) */}
			<Offcanvas
				show={showEditor}
				onHide={() => setShowEditor(false)}
				placement="bottom"
				style={{ height: 'auto', maxHeight: '50vh' }}
				backdrop={false}
				scroll={true}>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title>
						{isSimpleMode ? 'Simple Filter Editor' : 'Matrix Filter Editor'}
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					{isSimpleMode ? (
						<SimpleEditor
							filter={filter}
							updateFilter={updateFilter}
							classificationResults={classificationResults}
							currency={currency}
							currencies={currencies}
							propertyRange={propertyRange}
							availableProperties={availableProperties}
							coverageGaps={coverageGaps}
							projectNotes={projectNotes}
							setProjectNotes={setProjectNotes}
							isExporting={isExporting}
							onOpenExportModal={openExportModal}
						/>
					) : (
						<MatrixEditor
							filter={filter}
							updateFilter={updateFilter}
							classificationResults={classificationResults}
							currency={currency}
							currencies={currencies}
							bandPropertyLabel={bandPropertyLabel}
							thresholdPropertyLabel={thresholdPropertyLabel}
							availableProperties={availableProperties}
							bandRange={bandRange}
							thresholdRange={thresholdRange}
							coverageGaps={coverageGaps}
							projectNotes={projectNotes}
							setProjectNotes={setProjectNotes}
							isExporting={isExporting}
							onOpenExportModal={openExportModal}
						/>
					)}
				</Offcanvas.Body>
			</Offcanvas>

			{/* Export Options Modal */}
			<ExportReportModal
				show={showExportModal}
				onHide={() => setShowExportModal(false)}
				exportTheme={exportTheme}
				setExportTheme={setExportTheme}
				onExport={handleExport}
				isExporting={isExporting}
			/>

			{/* Save Template Modal */}
			<SaveTemplateModal
				show={showSaveModal}
				onHide={() => setShowSaveModal(false)}
				activeTemplate={activeTemplate}
				filterName={filter.name}
				onSave={saveTemplate}
				isPending={saveMutation.isPending}
			/>
		</div>
	);
});
